import { PhoneNumberUtil } from 'google-libphonenumber';
import React, { useCallback, useMemo, useState } from 'react';

import PhoneInput from '@/components/PhoneInput';
import { PriceSummary } from '@/components/SchedulingForm';
import { useRateDisplay } from '@/hooks/useRateDisplay';
import { RateDisplayType, toCreditsPerHour } from '@/utils/rateDisplay';

import type { OTPTransport, PhoneEntryStepProps } from '../../types';
import { useStyles } from './PhoneEntryStep.styles';

const phoneUtil = PhoneNumberUtil.getInstance();

export function PhoneEntryStep({
  expert,
  duration,
  hourlyRate,
  onSendCode,
  onBack,
  onLoginWithEmail,
  isLoading,
  error,
  initialPhone,
}: PhoneEntryStepProps): JSX.Element {
  const s = useStyles();
  const displayType = useRateDisplay();
  const showUSD = displayType === RateDisplayType.USD;

  // Local state - phone stores full international format with "+"
  const [phone, setPhone] = useState<string>(initialPhone || '');
  const [smsConsent, setSmsConsent] = useState(false);
  const [inputMode, setInputMode] = useState<'phone' | 'email'>('phone');
  const [emailAddress, setEmailAddress] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);

  // Handle email input change
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.target.value);
  }, []);

  // Validate phone number using google-libphonenumber
  // The PhoneInput component formats the number, we validate it
  const isPhoneValid = useMemo(() => {
    if (!phone || phone.length < 5) return false;
    try {
      const parsed = phoneUtil.parse(phone);
      // Use isPossibleNumber for lenient validation (faster)
      // For stricter validation, use isValidNumber() instead
      return phoneUtil.isPossibleNumber(parsed);
    } catch {
      return false;
    }
  }, [phone]);

  // Show validation error if phone has been touched and is invalid
  const hasPhoneValue = phone.length >= 5;
  const showPhoneError = phoneTouched && hasPhoneValue && !isPhoneValid;
  const phoneValidationError = showPhoneError ? 'Please enter a valid phone number' : undefined;

  // Validate email (basic validation)
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress);

  // Determine if form is valid
  const isFormValid = inputMode === 'phone' ? isPhoneValid && smsConsent : isEmailValid;

  // Handle send code
  const handleSendCode = useCallback(() => {
    if (!isFormValid || isLoading) return;

    if (inputMode === 'phone') {
      // Phone is already in E.164-compatible format
      onSendCode(phone, 'SMS' as OTPTransport);
    } else {
      onSendCode(emailAddress, 'EMAIL' as OTPTransport);
    }
  }, [isFormValid, isLoading, inputMode, phone, emailAddress, onSendCode]);

  // Toggle between phone and email input (commented out for V1 release)
  const _toggleInputMode = useCallback(() => {
    setInputMode(inputMode === 'phone' ? 'email' : 'phone');
  }, [inputMode]);

  return (
    <>
      <div className={s.formContent}>
        <p className={s.instructions}>
          {inputMode === 'phone'
            ? "Enter your mobile number to continue. We'll send you a verification code."
            : "Enter your email address to continue. We'll send you a verification code."}
        </p>

        {/* Error message */}
        {error && (
          <div className={s.errorMessage}>
            <p className={s.errorText}>{error}</p>
          </div>
        )}

        {inputMode === 'phone' ? (
          <>
            {/* Phone input - using existing PhoneInput component */}
            <div className={s.phoneSection}>
              <div className={s.phoneInputWrapper}>
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  onBlur={() => setPhoneTouched(true)}
                  disabled={isLoading}
                  placeholder="Enter phone number"
                  error={Boolean(error) || showPhoneError}
                  helperText={phoneValidationError}
                  showExampleOnError={showPhoneError}
                  fullWidth
                  label="Phone"
                  variant="outlined"
                  size="small"
                  type="tel"
                />
              </div>
            </div>

            {/* SMS consent checkbox */}
            <div className={s.consentSection}>
              <label className={s.consentLabel}>
                <input
                  type="checkbox"
                  className={s.consentCheckbox}
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  disabled={isLoading}
                />
                <span className={s.consentText}>
                  I agree to receive text messages from OnFrontiers, including one-time verification
                  codes and account notifications. Msg &amp; data rates may apply. Message frequency
                  varies. Reply STOP to opt out, HELP for help.
                </span>
              </label>
            </div>
          </>
        ) : (
          /* Email input */
          <div className={s.phoneSection}>
            <input
              id="email-input"
              type="email"
              className={s.emailInput}
              placeholder="you@company.com"
              value={emailAddress}
              onChange={handleEmailChange}
              disabled={isLoading}
              autoComplete="email"
            />
          </div>
        )}

        {/* Toggle to email/phone */}
        {/*
          // Commented out of V1 release
          <div className={s.loginLinkContainer}>
            <button
            type="button"
            className={s.loginLink}
            onClick={_toggleInputMode}
            disabled={isLoading}
            >
            {inputMode === 'phone' ? 'Use email instead →' : 'Use phone instead →'}
            </button>
          </div>
          */}

        {/* Link to existing login */}
        <div className={s.loginLinkContainer}>
          <button
            type="button"
            className={s.loginLink}
            onClick={onLoginWithEmail}
            disabled={isLoading}
          >
            Log in with email and password →
          </button>
        </div>

        {/* Price summary */}
        <PriceSummary
          duration={duration}
          pricing={
            showUSD
              ? { type: 'dollars', hourlyRate }
              : { type: 'credits', creditRate: toCreditsPerHour(expert.creditRate) }
          }
          showChargeNote
        />
      </div>

      {/* Footer with buttons */}
      <div className={s.footer}>
        <button type="button" className={s.backButton} onClick={onBack} disabled={isLoading}>
          Back
        </button>
        <button
          type="button"
          className={s.sendButton}
          onClick={handleSendCode}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? 'Sending...' : 'Send Code'}
        </button>
      </div>
    </>
  );
}

export default PhoneEntryStep;
